// theme/theme.js
//
// Wrap your app once with this theme and every MUI component (Typography
// variants, Button, Card, Chip, Paper, Dialog...) picks up the brand's
// colors, fonts, and radii automatically — no per-component color/font
// overrides needed unless you're deliberately deviating.
//
// Usage (in your root App/index file):
//   import { ThemeProvider, CssBaseline } from "@mui/material";
//   import theme from "./theme/theme";
//
//   <ThemeProvider theme={theme}>
//     <CssBaseline />
//     <App />
//   </ThemeProvider>

import { createTheme } from "@mui/material/styles";
import { tokens } from "./tokens";

const fontFamily = (key) =>
  key === "heading" ? tokens.font.heading : tokens.font.body;

const toMuiVariant = (entry) => ({
  fontFamily: fontFamily(entry.fontFamily),
  fontSize: entry.fontSize,
  lineHeight: entry.lineHeight,
  fontWeight: entry.fontWeight,
  letterSpacing: entry.letterSpacing,
  textTransform: entry.textTransform,
});

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: tokens.color.teal,
      dark: tokens.color.tealDark,
      light: tokens.color.tealLight,
      contrastText: tokens.color.white,
    },
    text: {
      primary: tokens.textOn.light.primary,
      secondary: tokens.textOn.light.secondary,
      disabled: tokens.textOn.light.disabled,
    },
    background: {
      default: tokens.bg.page,
      paper: tokens.bg.surface,
    },
    divider: tokens.color.line,
    success: { main: tokens.color.success },
    warning: { main: tokens.color.warning },
    error: { main: tokens.color.error },
    info: { main: tokens.color.info },
  },

  shape: {
    borderRadius: tokens.radius.md,
  },

  typography: {
    fontFamily: tokens.font.body,
    h1: toMuiVariant(tokens.type.h1),
    h2: toMuiVariant(tokens.type.h2),
    h3: toMuiVariant(tokens.type.h3),
    h4: toMuiVariant(tokens.type.h4),
    h5: toMuiVariant(tokens.type.h5),
    h6: toMuiVariant(tokens.type.h6),
    subtitle1: toMuiVariant(tokens.type.subtitle1),
    subtitle2: toMuiVariant(tokens.type.subtitle2),
    body1: toMuiVariant(tokens.type.body1),
    body2: toMuiVariant(tokens.type.body2),
    caption: toMuiVariant(tokens.type.caption),
    overline: toMuiVariant(tokens.type.overline),
    button: toMuiVariant(tokens.type.button),
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bg.page,
          color: tokens.textOn.light.primary,
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: tokens.button.radius,
          fontWeight: 600,
          textTransform: "none",
        },
        containedPrimary: {
          "&:hover": { backgroundColor: tokens.color.tealDark },
        },
        sizeSmall: {
          height: tokens.button.height.sm,
          padding: `0 ${tokens.button.paddingX.sm}px`,
          fontSize: tokens.button.fontSize.sm,
        },
        sizeMedium: {
          height: tokens.button.height.md,
          padding: `0 ${tokens.button.paddingX.md}px`,
          fontSize: tokens.button.fontSize.md,
        },
        sizeLarge: {
          height: tokens.button.height.lg,
          padding: `0 ${tokens.button.paddingX.lg}px`,
          fontSize: tokens.button.fontSize.lg,
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.sm },
      },
    },

    MuiTextField: {
      defaultProps: { size: "medium" },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: tokens.input.fontSize.md,
          backgroundColor: tokens.input.bg,
          borderRadius: tokens.input.radius,
        },
        sizeSmall: { fontSize: tokens.input.fontSize.sm },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: tokens.input.radius,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.input.border,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.input.borderHover,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.input.borderFocus,
            borderWidth: 1.5,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.input.borderError,
          },
        },
        input: {
          "&::placeholder": {
            color: tokens.input.placeholder,
            opacity: 1,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: tokens.input.label,
          "&.Mui-focused": { color: tokens.color.teal },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: { marginLeft: 2, fontSize: tokens.type.caption.fontSize },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: { borderRadius: tokens.input.radius },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.card.radius,
          border: tokens.card.elevated.border,
          boxShadow: tokens.card.elevated.shadowResting,
          transition: "box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease",
          // No padding here on purpose: <Card> and <CardContent> are two
          // separate nested boxes, so padding on both would stack (double
          // padding) for the standard <Card><CardContent>...</CardContent>
          // pattern used throughout this codebase. CardContent below is
          // where the real padding lives; if content ever sits directly
          // inside <Card> with no CardContent, add padding via sx on that
          // specific instance.
          "&:hover": {
            boxShadow: tokens.card.elevated.shadowHover,
            borderColor: tokens.card.elevated.borderHover,
          },
        },
      },
      // Custom variants so you can opt into the other two card presets with
      // e.g. <Card variant="outlined-dashed"> instead of hand-writing sx.
      variants: [
        {
          props: { variant: "outlined-dashed" },
          style: {
            background: tokens.card.outlined.background,
            border: tokens.card.outlined.border,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
              borderColor: tokens.card.outlined.borderHover,
            },
          },
        },
        {
          props: { variant: "flat" },
          style: {
            background: tokens.card.flat.background,
            border: tokens.card.flat.border,
            boxShadow: "none",
            "&:hover": { boxShadow: "none", borderColor: "transparent" },
          },
        },
      ],
    },

    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: tokens.card.padding.sm,
          [theme.breakpoints.up("sm")]: {
            padding: tokens.card.padding.md,
          },
          "&:last-child": {
            paddingBottom: tokens.card.padding.sm,
            [theme.breakpoints.up("sm")]: {
              paddingBottom: tokens.card.padding.md,
            },
          },
        }),
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: `${tokens.card.padding.sm}px`,
        },
      },
    },

    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: `${tokens.card.padding.sm}px`,
        },
      },
    },

    MuiPaper: {
      // Only affects direct <Paper>/<Container variant> usage — Menu,
      // Select, Popover, Tooltip etc. all render an internal <Paper> with
      // an explicit elevation prop of their own, so an explicit prop always
      // wins over this default and those stay untouched.
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none", backgroundColor: tokens.bg.surface },
        rounded: { borderRadius: tokens.radius.md },
        // elevation={0} is used everywhere in this codebase as "flat
        // section/filter-bar container" and has always needed a manual
        // border + borderColor added by hand (see the filter bars in
        // ExpertList/AgencyList/TopClientConnect). Centralizing that here
        // means new Paper usage gets it for free — existing manual
        // border/borderColor sx props still work, they just become
        // redundant and can be removed later.
        elevation0: {
          boxShadow: "none",
          border: `1px solid ${tokens.color.line}`,
        },
        elevation1: { boxShadow: tokens.shadow.sm },
        elevation2: { boxShadow: tokens.shadow.md },
        elevation3: { boxShadow: tokens.shadow.lg },
        outlined: {
          borderColor: tokens.color.line,
        },
      },
      // NOTE: deliberately no default padding on MuiPaper. Paper is the
      // base for Menu, Select dropdowns, Popover, Tooltip, and Accordion —
      // those manage their own internal spacing, and a theme-level padding
      // here would add unwanted space inside all of them. Pad individual
      // <Paper> instances with sx (or use <Card>/<CardContent>, which is
      // already themed with responsive padding above).
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.pill, fontWeight: 500 },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: tokens.radius.lg },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.sm },
      },
    },
  },
});

export default theme;